import { type FC, useEffect, useState } from "react";

import { CancelSaveMenu } from "components/menus/CancelSaveMenu";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Chip } from "mds/components/Chip";
import { IconText } from "mds/components/IconText";
import { TextField } from "mds/components/TextField";
import { Tooltip } from "mds/components/Tooltip";
import { AssessPointsMarkerIcon, ChevronDownIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";

interface PointsEditorProps {
  buttonLabel: string;
  buttonLabelShort?: string;
  containerQueryClassNameHide?: string;
  containerQueryClassNameShow?: string;
  disabled?: boolean;
  eventName: string;
  hideIcon?: boolean;
  initialPoints: number;
  inputMax?: string | number;
  inputStep?: string | number;
  inputTitle: string;
  onSave: (points: number) => void;
  page: PageType;
  parseInput: (input: string) => { value?: number; error?: string };
  readOnly?: boolean;
  saveTextKey?: "save" | "update";
  tooltipText?: string;
}

export const PointsEditor: FC<PointsEditorProps> = ({
  buttonLabel,
  buttonLabelShort,
  containerQueryClassNameHide,
  containerQueryClassNameShow,
  disabled = false,
  eventName,
  hideIcon = false,
  initialPoints,
  inputMax,
  inputStep,
  inputTitle,
  onSave,
  page,
  parseInput,
  readOnly = false,
  saveTextKey,
  tooltipText = "",
}) => {
  const { canAuthorSection } = useCapabilities();
  const [currentPoints, setCurrentPoints] = useState(initialPoints);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  useEffect(() => {
    // Keep the value in the text field up to date with external changes
    // to the points due to pubsub.
    setCurrentPoints(initialPoints);
  }, [initialPoints]);

  const onPointsChange = (input: string) => {
    const { value, error } = parseInput(input);
    if (value !== undefined) setCurrentPoints(value);
    setErrorMessage(error);
    setIsSaveDisabled(value === undefined || error !== undefined);
  };

  if (readOnly) {
    return (
      <Chip kind="white">
        <IconText
          containerQueryClassNameHide={containerQueryClassNameHide}
          containerQueryClassNameShow={containerQueryClassNameShow}
          iconStart={hideIcon ? undefined : <AssessPointsMarkerIcon />}
          shortText={buttonLabelShort}
          text={buttonLabel}
        />
      </Chip>
    );
  }

  return (
    <Tooltip content={disabled ? tooltipText : ""}>
      <CancelSaveMenu
        button={
          <Chip as="button" disabled={disabled} kind="white" title={!disabled ? tooltipText : ""}>
            <IconText
              containerQueryClassNameHide={containerQueryClassNameHide}
              containerQueryClassNameShow={containerQueryClassNameShow}
              iconEnd={canAuthorSection && !disabled && <ChevronDownIcon />}
              iconStart={hideIcon ? undefined : <AssessPointsMarkerIcon />}
              shortText={buttonLabelShort}
              text={buttonLabel}
            />
          </Chip>
        }
        isSaveDisabled={isSaveDisabled}
        saveTextKey={saveTextKey}
        title={t("glossary.points")}
        onCancel={() => onPointsChange(String(initialPoints))}
        onSave={() => onSave(currentPoints)}
      >
        <div className="flex w-full flex-col">
          <TextField
            className="w-full"
            eventName={eventName}
            eventProperties={{ pageTitle: page.title, pageId: page.id }}
            max={inputMax}
            min="0"
            placeholder={t("rubric_card.assessment_points", { count: 0 })}
            startIcon={<AssessPointsMarkerIcon />}
            step={inputStep}
            title={inputTitle}
            type="number"
            value={currentPoints}
            isEditing
            onChange={(event) => onPointsChange(event.target.value)}
          />

          {errorMessage && (
            <div className="body-xxs flex w-full justify-end text-red">{errorMessage}</div>
          )}
        </div>
      </CancelSaveMenu>
    </Tooltip>
  );
};
